<template>
    <div>
        <v-progress-linear v-if="!ajaxDone" :indeterminate="true"></v-progress-linear>

        <div v-if="ajaxDone">
            <div v-if="player.isAnonymous">
                <h1>Anonymous {{ $t('site.common.player') }}</h1>
                <v-alert :value="true" color="error" icon="error">
                    {{ $t('site.status.playerIsAnonymous') }}
                </v-alert>
            </div>
            <div v-else>
                <table style="margin-bottom: 5px;">
                    <tr>
                        <td><elv-avatar-icon :avatar="player.avatar" :isFriend="player.isFriend" width="64"/></td>
                        <td>&nbsp;&nbsp;</td>
                        <td><h1>{{ player.username }} <elv-user-badges :player="player"></elv-user-badges> </h1>
                            <h2 class="subtitle">{{ player.race === "humans" ? $t(`site.common.human`) : $t("site.common.elf") }},    <router-link :to="`/fellowship/${player.server}/${player.fellowshipId}`">{{ player.fellowship }}</router-link> on <elv-server-name :server="player.server"/></h2>
                        </td>
                    </tr>
                </table>

                <elv-check-back-when-more-info :object="player"></elv-check-back-when-more-info>

                <div class="row">
                    <div class="col-md-2">
                        <div class="panel">
                            <div class="panel-content">
                                <h1 style="text-align:center;">{{ player.rank }}</h1>
                            </div>
                            <div class="panel-footer bg-dark"><h3><strong>{{ $t('site.fellowship.stats.rank') }}</strong></h3></div>
                        </div>
                    </div>
                    <div class="col-md-2">
                        <div class="panel">
                            <div class="panel-content">
                                <h1><vue-numeric :value="player.currScore"/></h1>
                            </div>
                            <div class="panel-footer bg-dark"><h3><strong>{{ $t('site.common.score') }}</strong></h3></div>
                        </div>
                    </div>
                    <div class="col-md-2">
                        <div class="panel">
                            <div class="panel-content player-detail-box">
                                <elv-boost-icon :boost="player.boost1" width="40"/>
                                <elv-boost-icon :boost="player.boost2" width="36"/>
                                <elv-boost-icon :boost="player.boost3" width="36"/>
                            </div>
                            <div class="panel-footer bg-dark"><h3><strong>{{ $t('site.common.boosts') }}</strong></h3></div>
                        </div>
                    </div>
                    <div class="col-md-2">
                        <div class="panel">
                            <div class="panel-content">
                                <h1 style="text-align:center;">{{player.technologySection}}</h1>
                            </div>
                            <div class="panel-footer bg-dark"><h3><strong>{{ $t('site.common.chapter') }}</strong></h3></div>
                        </div>
                    </div>
                    <div class="col-md-2">
                        <div class="panel">
                            <div class="panel-content">
                                <h1 style="text-align:center;">{{player.unlockedAreas + 5}}</h1>
                            </div>
                            <div class="panel-footer bg-dark"><h3><strong>{{ $t('site.common.expansions') }}</strong></h3></div>
                        </div>
                    </div>
                    <div class="col-md-2">
                        <div class="panel">
                            <div class="panel-content">
                                <h1 style="text-align:center;">{{player.tournamentAverage4W.toFixed(0)}}</h1>
                            </div>
                            <div class="panel-footer bg-dark"><h3 v-html="$t('site.tournamentAverage4W')" :title="$t('site.tournamentAverage4WTip')"></h3></div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-lg-6">
                        <div class="panel">
                            <highcharts :options="scoreHistoryChart"></highcharts>
                        </div>
                    </div>
                    <div class="col-lg-6">
                        <div class="panel">
                            <highcharts :options="tournamentHistoryChart"></highcharts>
                        </div>
                    </div>
                </div>

                <v-layout row wrap :align-center="true">
                    <v-flex xs3></v-flex>
                    <v-flex xs6 :align-center="true">
                        <v-card>
                            <elv-architect-redirector :player="player" :showLogo="true"/>
                        </v-card>
                    </v-flex>
                    <v-flex xs3></v-flex>
                </v-layout>

                <h2>{{ $t('site.fellowship.coinsAndSuppliesTable.heading') }}</h2>
                <small>{{ $t('site.fellowship.coinsAndSuppliesTable.description') }}
                </small>
                <elv-other-production-table :members="[player]"></elv-other-production-table>

                <h2>{{ $t('site.fellowship.popCultureTable.heading') }}</h2>
                <small>{{ $t('site.fellowship.popCultureTable.description') }}
                </small>
                <elv-pop-culture-table :members="[player]"></elv-pop-culture-table>

                <h2>{{ $t('site.fellowship.cityCoverageTable.heading') }}</h2>
                <small>{{ $t('site.fellowship.cityCoverageTable.description') }}</small>
                <elv-tiles-table :members="[player]"></elv-tiles-table>

                <h2>{{ $t('site.player.fellowshipHistory.heading') }} </h2>
                <div class="row">
                    <div class="col-lg-12">
                        <div class="panel">
                            <v-data-table :items="player.fellowshipHistory"
                                          :headers="memberHistoryHeaders"
                                          hide-actions
                                          class="elevation-1"
                            >
                                <template slot="items" slot-scope="props">
                                    <td>{{props.item.name || props.item.fellowship}}
                                    <td>{{props.item.date | yyyymmdd }}</td>
                                </template>
                                <template slot="no-data">
                                    <v-alert :value="true" color="info" icon="info">
                                        {{ $t('site.fellowship.notifications.noChangesSinceTrackingBegan') }}
                                    </v-alert>
                                </template>
                            </v-data-table>
                        </div>
                    </div>
                </div>

                <center><h2>{{ $t('site.common.lastUpdated') }}: <elv-refresh-user-icon :player="player"></elv-refresh-user-icon></h2></center>
            </div>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import VueHighcharts from 'vue-highcharts';
    import Highcharts from 'highcharts';
    import _ from 'lodash';
    import ElvBoostIcon from '@/components/ElvBoostIcon';
    import ElvServerName from '@/components/ElvServerName';
    import RelativeTimeFormatter from '@/components/RelativeTimeFormatter';
    import VueNumeric from 'vue-numeric';
    let elvenStats = require('../lib/elven-stats');
    let elvenCharts = require('../lib/elven-charts');
    import axios from 'axios';
    import moment from 'moment';
    import ElvPopCultureTable from "../components/ElvPopCultureTable"
    import ElvOtherProductionTable from "../components/ElvOtherProductionTable"
    import ElvAvatarIcon from "../components/ElvAvatarIcon"
    import ElvArchitectRedirector from "../components/ElvArchitectRedirector"
    import ElvCheckBackWhenMoreInfo from "@/components/ElvCheckBackWhenMoreInfo"
    import ElvRefreshUserIcon from "@/components/ElvRefreshUserIcon"
    import ElvTilesTable from "@/components/ElvTilesTable"
    import ElvUserBadges from "@/components/ElvUserBadges";



    Vue.use(VueHighcharts, { Highcharts: Highcharts });

    export default {
        name: 'PlayerDetail',
        props: ['playerId', 'server'],
        components: {
            ElvUserBadges,
            ElvTilesTable,
            ElvRefreshUserIcon,
            ElvCheckBackWhenMoreInfo,
            ElvArchitectRedirector,
            ElvAvatarIcon,
            ElvOtherProductionTable,
            ElvPopCultureTable,
            VueNumeric, ElvBoostIcon, ElvServerName, RelativeTimeFormatter},
        data() {
            return {
                ajaxDone: false,
                player: {},
                scoreHistoryChart: {},
                tournamentHistoryChart: {},
            }
        },
        watch: {
            // call again the method if the route changes
            '$route': 'fetchData'
        },
        created() {
            Event.$on("playerRefreshed", (data) => {
                if( data.playerId === this.player.playerId ) {
                    console.error("Refreshed Player in PlayerDetail: " + data.username);
                    this.loadData(data);
                } else {
                    console.log("Ignoring refresh event as it's for different player: ", data);
                }
            });
            this.fetchData();
        },
        methods: {
            fetchData: function() {
                try {
                    console.log("Calling fetch player... ");
                    axios.get(`/api/player/${this.server}/${this.playerId}`).then(( response ) => {
                        //console.log("PLAYER RESPONSE", response);
                        this.loadData(response.data);
                    }).catch(( err ) => {
                        console.log(err);
                        this.ajaxDone = true;
                        //Event.$emit("ajaxError", "Error");
                    });
                }catch(err) {
                    console.error(err);
                }
            },
            loadData: function( player ) {
                this.player = player;
                this.scoreHistoryChart = elvenCharts.getScoreHistoryChart( this.player, this.$i18n, this.Highcharts, new Date(2017, 12, 1) );
                this.tournamentHistoryChart = elvenCharts.getTournamentHistoryChart( this.player, this.$i18n, this.Highcharts, new Date(2017, 12, 1) );
                _.set( this, "player.cityReport.cultureBoostPercent", elvenStats.computeCultureBoostPercentForUser(this.player) * 100);
                player.tournamentAverage4W = elvenStats.getLast4TournamentsAvg(player);
                this.ajaxDone = true;
            }
        },
        computed:  {
            memberHistoryHeaders: {
                get: function() {
                    return [
                        {
                            text: this.$i18n.t('site.common.fellowship'),
                            sortable: false,
                            value: "fellowship"
                        },
                        {
                            text: this.$i18n.t('site.common.date'),
                            field: "date",
                            sortable: false,
                        }
                    ];
                }
            }
        },
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
    .subtitle {
        margin-top: -13px;
    }
</style>



// WEBPACK FOOTER //
// src/views/PlayerDetail.vue